import { useState } from "react";
import { Check } from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { Stagger, StaggerItem } from "@/components/motion";
import CTAButton from "@/components/CTAButton";
import { PRICING_PLANS, CURRENCIES, ANNUAL_MONTHS_FREE } from "@/lib/site";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";

export function CurrencySwitcher() {
  const { code, setCode } = useCurrency();
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1" data-testid="currency-switcher">
      {CURRENCIES.map((c) => (
        <button
          key={c.code}
          onClick={() => setCode(c.code)}
          data-testid={`currency-${c.code}`}
          className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-xs font-semibold transition-colors ${
            code === c.code ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
          }`}
        >
          <span>{c.flag}</span> {c.code}
        </button>
      ))}
    </div>
  );
}

export function BillingSwitcher({ billing, setBilling }) {
  return (
    <div className="inline-flex items-center gap-1 rounded-full border border-border bg-card p-1" data-testid="billing-switcher">
      <button
        onClick={() => setBilling("monthly")}
        data-testid="billing-monthly"
        className={`rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
          billing === "monthly" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Monthly
      </button>
      <button
        onClick={() => setBilling("annual")}
        data-testid="billing-annual"
        className={`inline-flex items-center gap-1.5 rounded-full px-4 py-1.5 text-xs font-semibold transition-colors ${
          billing === "annual" ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground"
        }`}
      >
        Annual
        <span className="rounded-full bg-brand-teal/20 px-1.5 py-0.5 text-[10px] font-bold text-brand-teal">
          Save {ANNUAL_MONTHS_FREE} Months
        </span>
      </button>
    </div>
  );
}

function parseNum(s) {
  if (!s || typeof s !== "string") return null;
  const n = Number(String(s).replace(/[, ]/g, ""));
  return Number.isFinite(n) ? n : null;
}

function fmtNum(n) {
  if (n == null) return "";
  return n % 1 === 0 ? n.toLocaleString() : n.toFixed(2);
}

function Price({ plan, billing }) {
  const { currency } = useCurrency();
  if (plan.prices === "contact") {
    return <p className="font-display text-3xl font-semibold tracking-tight">Let's talk</p>;
  }
  const priceStr = plan.prices[currency.code];
  if (priceStr === "Free") {
    return (
      <div>
        <p className="font-display text-3xl font-semibold tracking-tight">
          <span className="brand-gradient-text">Free</span>
        </p>
        <p className="mt-1 text-xs text-muted-foreground">Forever — no credit card required</p>
      </div>
    );
  }
  const monthly = parseNum(priceStr);
  const isAnnual = billing === "annual";
  // Annual = 10 months' price paid up-front for the year
  const annualTotal = monthly != null ? monthly * (12 - ANNUAL_MONTHS_FREE) : null;
  const perMonth = isAnnual && annualTotal != null ? annualTotal / 12 : monthly;
  return (
    <div>
      <div className="flex items-baseline gap-2">
        <p className="font-display text-3xl font-semibold tracking-tight">
          {currency.symbol}{fmtNum(perMonth)}
        </p>
        <span className="text-sm text-muted-foreground">/mo</span>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {isAnnual
          ? <>/mo · billed annually at {currency.symbol}{fmtNum(annualTotal)}</>
          : <>Billed monthly</>}
      </p>
      {isAnnual && (
        <p className="mt-1 text-[11px] font-medium text-brand-teal">
          Save {ANNUAL_MONTHS_FREE} months when you pay yearly
        </p>
      )}
    </div>
  );
}

export default function PricingCards() {
  const [billing, setBilling] = useState("monthly");
  const navigate = useNavigate();
  const { user } = useAuth();

  const onPaidCta = (plan) => {
    // Route to signup if unauthenticated (with plan intent preserved); otherwise open the upgrade flow.
    if (!user) {
      navigate(`/signup?next=${encodeURIComponent(`/app/settings/billing?plan=${plan.plan}&billing=${billing}`)}`);
    } else {
      navigate(`/app/settings/billing?plan=${plan.plan}&billing=${billing}`);
    }
  };

  return (
    <div>
      <div className="mb-8 flex flex-col items-center gap-4">
        <BillingSwitcher billing={billing} setBilling={setBilling} />
      </div>
      <Stagger className="grid gap-5 md:grid-cols-2 lg:grid-cols-4" amount={0.1}>
        {PRICING_PLANS.map((plan) => (
          <StaggerItem key={plan.name} className="h-full">
            <div
              data-testid={`pricing-card-${plan.name.toLowerCase()}`}
              className={`card-hover relative flex h-full flex-col rounded-3xl border p-7 ${
                plan.highlight
                  ? "border-brand-teal/40 bg-card shadow-[0_30px_70px_-30px_rgba(13,148,136,0.4)]"
                  : "border-border bg-card"
              }`}
            >
              {plan.highlight && (
                <span className="absolute -top-3 left-7 rounded-full brand-gradient-bg px-3 py-1 text-[11px] font-semibold text-white">
                  Most popular
                </span>
              )}
              <h3 className="font-display text-lg font-semibold tracking-tight">{plan.name}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{plan.tagline}</p>
              <div className="mt-6"><Price plan={plan} billing={billing} /></div>
              <ul className="mt-6 flex-1 space-y-3">
                {plan.outcomes.map((f) => (
                  <li key={f} className="flex items-start gap-2.5 text-sm text-muted-foreground">
                    <Check className="mt-0.5 h-4 w-4 shrink-0 text-brand-teal" />
                    {f}
                  </li>
                ))}
              </ul>
              <div className="mt-7">
                {plan.plan ? (
                  <button
                    onClick={() => onPaidCta(plan)}
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                    className={`inline-flex w-full items-center justify-center rounded-full px-5 py-2.5 text-sm font-semibold transition-all hover:-translate-y-0.5 ${
                      plan.highlight ? "brand-gradient-bg text-white" : "border border-border bg-background text-foreground hover:border-foreground/30"
                    }`}
                  >
                    {plan.cta}
                  </button>
                ) : (
                  <CTAButton
                    to={plan.ctaTo}
                    variant={plan.highlight ? "gradient" : "outline"}
                    size="md"
                    className="w-full"
                    data-testid={`pricing-cta-${plan.name.toLowerCase()}`}
                  >
                    {plan.cta}
                  </CTAButton>
                )}
              </div>
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </div>
  );
}
